import { Col, Row } from 'antd';
import DownloadIcon from 'icons/download.svg';
import PrintIcon from 'icons/print.svg';
import dayjs from 'mixins/dayjsConfig.js';
import { localize } from 'mixins/helpers';
import Button from 'mixins/ui/button/button';
import CmsTable from 'mixins/ui/cms-table';
import PrintForm2 from 'mixins/ui/print-form-2';
import React from 'react';
import { downloadDebtorInfo } from './actions';

export const handleDownload = (values: Record<string, string>) => {
    downloadDebtorInfo(values).then(({ data }) => {
        const filename = `Debtors_${values.language}.xlsx`;
        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
    });
};

const Results = ({
    type,
    translations,
    params,
    tableData,
    newValues,
    total,
    onPageChange,
}: {
    type: string;
    translations: Record<string, string>;
    params: Object;
    tableData: Array<any>;
    newValues: Record<string, any>;
    total: number;
    onPageChange: (page: number, pageSize: number) => void;
}) => {
    const columns = [
        {
            title: localize('region', translations),
            key: 'region',
            dataIndex: ['region', 'nameRu'],
            type: ['BANKRUPTCY', 'REHABILITATION', 'LIQUIDATION'],
        },
        {
            title: localize('find-debtor-debtor-name', translations),
            key: 'find-debtor-debtor-name',
            dataIndex: 'name',
            render: (name, row) =>
                name?.nameRu ||
                `${row?.lastName || ''} ${row?.firstName || ''} ${row?.middleName || ''}`,
            type: ['BANKRUPTCY', 'REHABILITATION', 'LIQUIDATION'],
            width: 400,
        },
        {
            title: localize('iin', translations),
            key: 'iin',
            dataIndex: 'taxpayerCode',
            type: ['BANKRUPTCY', 'REHABILITATION', 'LIQUIDATION'],
        },
        {
            title: localize('find-debtor-activity-type', translations),
            key: 'find-debtor-activity-type',
            dataIndex: ['activity', 'nameRu'],
            type: ['BANKRUPTCY', 'REHABILITATION', 'LIQUIDATION'],
            width: 300,
        },
        {
            title: localize('find-debtor-manager-name', translations),
            key: 'find-debtor-manager-name',
            type: ['BANKRUPTCY', 'REHABILITATION', 'LIQUIDATION'],
            render: (_, row) =>
                `${row?.directorLastName || ''} ${row?.directorFirstName || ''} ${
                    row?.directorMiddleName || ''
                }`,
        },
        {
            title: localize('find-debtor-founder-name', translations),
            key: 'find-debtor-founder-name',
            render: (_, row) => {
                if (Array.isArray(row.founders)) {
                    return (row.founders as Array<any>)
                        .map((founder) => {
                            const fullName = `${founder?.founderLastName || ''} ${
                                founder?.founderFirstName || ''
                            } ${founder?.founderMiddleName || ''}`;
                            const name = founder.founder?.nameRu;

                            return name || fullName;
                        })
                        .join(', ');
                } else {
                    return '';
                }
            },
            type: ['BANKRUPTCY', 'REHABILITATION', 'LIQUIDATION'],
        },
        {
            title: localize('find-debtor-liquidation-decision-date', translations),
            key: 'find-debtor-liquidation-decision-date',
            dataIndex: 'issueDate',
            type: ['LIQUIDATION', 'REHABILITATION'],
            render: (date) => (date ? dayjs(date).format('DD.MM.YYYY') : ''),
            width: 100,
        },
        {
            title: localize('find-debtor-bankruptcy-decision-date', translations),
            key: 'find-debtor-bankruptcy-decision-date',
            dataIndex: 'issueDate',
            type: ['BANKRUPTCY'],
            render: (date) => (date ? dayjs(date).format('DD.MM.YYYY') : ''),
            width: 100,
        },
        {
            title: localize('find-debtor-rehabilitation-decision-effective-date', translations),
            key: 'find-debtor-rehabilitation-decision-effective-date',
            dataIndex: 'entryForceDate',
            type: ['REHABILITATION'],
            render: (date) => (date ? dayjs(date).format('DD.MM.YYYY') : ''),
            width: 100,
        },
        {
            title: localize('find-debtor-bankruptcy-decision-effective-date', translations),
            key: 'find-debtor-bankruptcy-decision-effective-date',
            dataIndex: 'entryForceDate',
            type: ['BANKRUPTCY'],
            render: (date) => (date ? dayjs(date).format('DD.MM.YYYY') : ''),
            width: 100,
        },
        {
            title: localize('find-debtor-liquidation-effective-date', translations),
            key: 'find-debtor-liquidation-effective-date',
            dataIndex: 'entryForceDate',
            type: ['LIQUIDATION'],
            render: (date) => (date ? dayjs(date).format('DD.MM.YYYY') : ''),
            width: 100,
        },
        {
            title: localize('find-debtor-rehabilitation-manager-full-name-registry', translations),
            key: 'find-debtor-rehabilitation-manager-full-name-registry',
            dataIndex: 'admin',
            render: (admin) =>
                admin
                    ? `${admin?.lastname || ''} ${admin?.firstname || ''} ${
                          admin?.middlename || ''
                      }`
                    : '',
            type: ['REHABILITATION'],
            width: 250,
        },
        {
            title: localize('find-debtor-rehabilitation-manager-full-name-owner', translations),
            key: 'find-debtor-rehabilitation-manager-full-name-owner',
            dataIndex: 'physPerson',
            render: (physPerson) =>
                physPerson
                    ? `${physPerson?.lastname || ''} ${physPerson?.firstname || ''} ${
                          physPerson?.middlename || ''
                      }`
                    : '',
            type: ['REHABILITATION'],
            width: 250,
        },
        {
            title: localize('find-debtor-administrator-full-name', translations),
            key: 'find-debtor-administrator-full-name',
            render: (_, record) =>
                `${record.adminLastName || ''} ${record.adminFirstName || ''} ${
                    record.adminMiddleName || ''
                }`,
            type: ['BANKRUPTCY'],
            width: 250,
        },
        {
            title: localize('find-debtor-administrator-contact', translations),
            key: 'find-debtor-administrator-contact',
            type: ['BANKRUPTCY', 'REHABILITATION'],
            children: [
                {
                    title: localize('find-debtor-email-address', translations),
                    dataIndex: 'adminEmail',
                    key: 'adminEmail',
                    render: (adminEmail, row) =>
                        type === 'BANKRUPTCY'
                            ? adminEmail
                            : row.admin
                            ? row.admin.email || ''
                            : row.physPerson
                            ? row.physPerson.email || ''
                            : '',
                },
                {
                    title: localize('find-debtor-phone-number', translations),
                    dataIndex: 'adminPhone',
                    key: 'adminPhone',
                    render: (adminPhone, row) =>
                        type === 'BANKRUPTCY'
                            ? adminPhone
                            : row.admin
                            ? row.admin.phone || ''
                            : row.physPerson
                            ? row.physPerson.phone || ''
                            : '',
                },
                {
                    title: localize('find-debtor-business-address', translations),
                    dataIndex: 'adminAddress',
                    key: 'adminAddress',
                    render: (adminAddress, row) =>
                        type === 'BANKRUPTCY'
                            ? adminAddress?.nameRu || ''
                            : row.admin
                            ? row.admin.address?.nameRu || ''
                            : row.physPerson
                            ? row.physPerson.address?.nameRu || ''
                            : '',
                    width: 300,
                },
            ],
        },
    ];
    return (
        <>
            <CmsTable
                translations={translations}
                type="default"
                dataSource={tableData}
                columns={columns.filter((el) => el.type && el.type.includes(type))}
                size="large"
                scroll={{
                    x: 'max-content',
                }}
                total={total}
                pagination={{
                    onChange: onPageChange,
                }}
            />
            {tableData.length > 0 && (
                <Row
                    gutter={15}
                    justify="end"
                    className="mt-3"
                >
                    <Col>
                        <Button
                            type="primary"
                            color="gold"
                            icon={<DownloadIcon />}
                            onClick={async () => {
                                // const isVerified = await executeRecaptcha(
                                //     'find_debtor_download_results'
                                // );

                                // if (!isVerified) {
                                //     return;
                                // }

                                handleDownload(newValues);
                            }}
                        >
                            {localize('button.download', translations)}
                        </Button>
                    </Col>
                    <Col>
                        <PrintForm2
                            apiUrl="/services/isnaportal/open-api/debtor/generate/pdf"
                            isPost
                            params={params}
                            icon={<PrintIcon />}
                            translations={translations}
                        />
                    </Col>
                </Row>
            )}
        </>
    );
};

export default Results;
